import * as THREE from 'three';
import type { V2 } from '../core/math';
import { keyLabel } from './keyLabel';

/** Logical game actions that keys can be bound to. */
export type GameAction =
  | 'skillQ' | 'skillW' | 'skillE' | 'skillR' | 'skillA' | 'skillS' | 'skillD' | 'skillF'
  | 'dash' | 'identityZ' | 'identityX' | 'awakening' | 'basic'
  | 'item1' | 'item2' | 'item3' | 'item4' | 'interact'
  | 'inventory' | 'character' | 'skills' | 'map' | 'quests' | 'sigils' | 'cards' | 'journal'
  | 'emotes' | 'party' | 'settings' | 'mount' | 'chat' | 'overlayMap' | 'hideUi' | 'bounties' | 'bonds' | 'haven' | 'market' | 'lifeHud';

export const DEFAULT_BINDINGS: Record<GameAction, string> = {
  skillQ: 'KeyQ', skillW: 'KeyW', skillE: 'KeyE', skillR: 'KeyR',
  skillA: 'KeyA', skillS: 'KeyS', skillD: 'KeyD', skillF: 'KeyF',
  dash: 'Space', identityZ: 'KeyZ', identityX: 'KeyX', awakening: 'KeyV', basic: 'KeyC',
  item1: 'Digit1', item2: 'Digit2', item3: 'Digit3', item4: 'Digit4', interact: 'KeyG',
  inventory: 'KeyI', character: 'KeyP', skills: 'KeyK', map: 'KeyM', quests: 'KeyJ',
  sigils: 'KeyN', cards: 'KeyL', journal: 'KeyO', emotes: 'KeyY', party: 'KeyU', settings: 'Escape',
  mount: 'Backquote', chat: 'Enter', overlayMap: 'Tab', hideUi: 'KeyH', bounties: 'KeyB', bonds: 'KeyT', haven: 'KeyU+alt', market: 'KeyY+alt', lifeHud: 'KeyB+alt',
};

export interface InputListener {
  onAction(action: GameAction, pressed: boolean, repeat: boolean): void;
  /** Primary mouse = move (or attack when swapped). */
  onPointer(button: 'move' | 'attack', pressed: boolean): void;
  onWheel(deltaY: number): void;
}

/**
 * Keyboard + mouse. Keys map to actions through rebindable codes (optionally
 * with an "alt" modifier suffix). The pointer is tracked in normalised device
 * coordinates and resolved to a ground point every frame by the game.
 */
export class Input {
  bindings: Record<GameAction, string> = { ...DEFAULT_BINDINGS };
  /** When true the left button attacks and the right button moves. */
  swapMouse = false;
  readonly ndc = new THREE.Vector2();
  readonly held = new Set<string>();
  moveHeld = false;
  attackHeld = false;
  enabled = true;
  /** Set by UI when a text field has focus. */
  typing = false;
  private listener: InputListener | null = null;
  private reverse = new Map<string, GameAction>();
  private raycaster = new THREE.Raycaster();

  constructor(readonly element: HTMLElement) {
    this.rebuild();
    window.addEventListener('keydown', (e) => this.key(e, true));
    window.addEventListener('keyup', (e) => this.key(e, false));
    element.addEventListener('pointermove', (e) => this.pointer(e));
    element.addEventListener('pointerdown', (e) => this.button(e, true));
    window.addEventListener('pointerup', (e) => this.button(e, false));
    element.addEventListener('contextmenu', (e) => e.preventDefault());
    element.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.listener?.onWheel(e.deltaY);
    }, { passive: false });
    window.addEventListener('blur', () => {
      this.held.clear();
      this.moveHeld = false;
      this.attackHeld = false;
    });
  }

  setListener(l: InputListener): void {
    this.listener = l;
  }

  setBindings(b: Partial<Record<GameAction, string>>): void {
    this.bindings = { ...DEFAULT_BINDINGS, ...b };
    this.rebuild();
  }

  private rebuild(): void {
    this.reverse.clear();
    for (const [action, code] of Object.entries(this.bindings) as [GameAction, string][]) this.reverse.set(code, action);
  }

  bindingLabel(action: GameAction): string {
    return keyLabel(this.bindings[action]);
  }

  private key(e: KeyboardEvent, down: boolean): void {
    if (!this.enabled) return;
    const target = e.target as HTMLElement | null;
    const inField = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
    if (inField || this.typing) {
      if (down && e.code === 'Escape') target?.blur();
      return;
    }
    const code = e.altKey ? `${e.code}+alt` : e.code;
    const action = this.reverse.get(code) ?? (e.altKey ? undefined : undefined);
    if (!action) return;
    e.preventDefault();
    if (down) this.held.add(e.code);
    else this.held.delete(e.code);
    this.listener?.onAction(action, down, e.repeat);
  }

  private pointer(e: PointerEvent): void {
    const r = this.element.getBoundingClientRect();
    this.ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
  }

  private button(e: PointerEvent, down: boolean): void {
    if (!this.enabled) return;
    if (down && e.target !== this.element) return;
    this.pointer(e);
    const primary = e.button === 0;
    const secondary = e.button === 2;
    if (!primary && !secondary) return;
    const isMove = this.swapMouse ? secondary : primary;
    if (isMove) {
      if (!down && !this.moveHeld) return;
      this.moveHeld = down;
      this.listener?.onPointer('move', down);
    } else {
      if (!down && !this.attackHeld) return;
      this.attackHeld = down;
      this.listener?.onPointer('attack', down);
    }
  }

  /**
   * Intersects the pointer ray with the terrain. Starts from the y=0 plane
   * and refines against the heightfield (converges quickly for gentle terrain).
   */
  groundPoint(camera: THREE.Camera, heightAt: (x: number, z: number) => number, out: V2 = { x: 0, z: 0 }): V2 {
    this.raycaster.setFromCamera(this.ndc, camera);
    const o = this.raycaster.ray.origin;
    const d = this.raycaster.ray.direction;
    let h = 0;
    let x = o.x;
    let z = o.z;
    for (let i = 0; i < 5; i++) {
      if (Math.abs(d.y) < 1e-5) break;
      const t = (h - o.y) / d.y;
      x = o.x + d.x * t;
      z = o.z + d.z * t;
      h = heightAt(x, z);
    }
    out.x = x;
    out.z = z;
    return out;
  }

  /** Ray for picking objects (NPCs, gatherables) under the cursor. */
  ray(camera: THREE.Camera): THREE.Ray {
    this.raycaster.setFromCamera(this.ndc, camera);
    return this.raycaster.ray;
  }
}
